import {
  useCallback, useEffect, useMemo, useState, useTransition,
} from 'react';
import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import {
  getFormulaDemographicIds, isReportFormulaIsTooComplex,
} from '~/exportReport/template/reportTemplate.helpers';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { createRadiusFilterItemRequest } from '~/spreadsheet/filter/radius/spreadsheetFilterRadius.factory';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import {
  type DemographicsQueryBulkResults, getDemographicsQueryBulk,
} from '~/store/demographics/demographics.repository';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';

type UseExportReportDataParams = {
  isPreviewMode?: boolean;
  template: ReportTemplate | null;
};

export const useExportReportData = ({ isPreviewMode = false, template }: UseExportReportDataParams) => {
  const [data, setData] = useState<DemographicsQueryBulkResults | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, startTransition] = useTransition();
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const boundaryGroups = useBoundaryGroupsSelector();
  // in the future we will have a way to specify the BG, but for now use ags-us-zip-codes as a fallback
  const boundaryGroupId = useMemo(() => {
    return boundaryGroups.find(boundaryGroup => boundaryGroup.codename === 'ags-us-zip-codes') ?.id ?? null;
  }, [boundaryGroups]);
  const exportReportState = useToolsStateExportReportSelector();

  const spreadsheetData = useLatLngSpreadsheetData();

  const center = useMemo(() => {
    if (!exportReportState.isActive || exportReportState.params.type !== ExportReportType.Location) {
      return null;
    }

    const rowData = spreadsheetData.getRow(exportReportState.params.data.markerId);

    return rowData ?? null;
  }, [exportReportState, spreadsheetData]);

  const getDemographicValue = useCallback((columnId: string, demographicId: string): number | null => {
    if (isPreviewMode) {
      return null;
    }

    if (!data || !data[columnId]) {
      return null;
    }

    const demographicData = data[columnId].data.find(demographic => demographic.demographicId.replace('%', '') === demographicId.replace('%', ''));

    if (!demographicData) {
      return null;
    }

    if (demographicId.includes('%')) {
      return demographicData.valueOfTotal ?? null;
    }

    return demographicData.value;
  }, [data, isPreviewMode]);

  const getDemographicLabel = useCallback((demographicId: string): string | null => {
    if (!data) {
      return null;
    }

    for (const item of Object.values(data)) {
      if (!item.isSuccess) {
        continue;
      }

      for (const demographic of item.data) {
        if (demographic.demographicId.replace('%', '') === demographicId.replace('%', '')) {
          return demographic.name;
        }
      }
    }

    return null;
  }, [data]);

  useEffect(() => {
    setIsError(false);

    if (!template || !center || !clientId || !mapId || isPreviewMode) {
      return;
    }

    const demographicIds: string[] = [...template.items.reduce<Set<string>>((acc, item) => {
      if (item.type !== 'table') {
        return acc;
      }

      item.content.forEach(row => {
        if (row.type === 'data-row') {
          if (row.formula) {
            // skip the demographic if formula is too complex
            if (isReportFormulaIsTooComplex(row.formula)) {
              return;
            }
            else {
              for (const demographicId of getFormulaDemographicIds(row.formula.replace('%', ''))) {
                acc.add(demographicId);
              }
            }
          }
          else {
            // remove percentage sign from the request. If we need the percentage, then it will be returned as a property for regular demographic ID
            acc.add(row.id.replace('%', ''));
          }
        }
      });

      return acc;
    }, new Set())];

    startTransition(async () => {
      try {
        const results = await getDemographicsQueryBulk(clientId,
          {
            queries: [{
              id: 'radius5',
              map_id: mapId,
              demographics_ids: demographicIds,
              boundary_group_id: boundaryGroupId ?? undefined,
              radius_filter: {
                type: 'or',
                radiuses: [createRadiusFilterItemRequest({
                  radius: 5,
                  center,
                  unit: UnitSystem.metric,
                })],
              },
            },
            {
              id: 'radius10',
              map_id: mapId,
              demographics_ids: demographicIds,
              radius_filter: {
                type: 'or',
                radiuses: [createRadiusFilterItemRequest({
                  radius: 10,
                  center,
                  unit: UnitSystem.metric,
                })],
              },
            },
            {
              id: 'radius15',
              map_id: mapId,
              demographics_ids: demographicIds,
              radius_filter: {
                type: 'or',
                radiuses: [createRadiusFilterItemRequest({
                  radius: 15,
                  center,
                  unit: UnitSystem.metric,
                })],
              },
            },
            ],
          });

        setData(results);
      }
      catch {
        setIsError(true);
      }
    });
  }, [center, template, clientId, mapId, isPreviewMode, boundaryGroupId]);

  useEffect(() => {
    if (!data) {
      return;
    }

    Object.assign(window, {
      isReportFullyLoaded: true,
    });
  }, [data]);

  return {
    isLoading,
    isError,
    getDemographicValue,
    getDemographicLabel,
  };
};
